// App-scoped disk attachments table. Lists all non-deprovisioned disk
// attachments across every service of an app. Reads the org-wide attachments
// via fetchDiskAttachments, then filters to the attachments whose serviceId
// belongs to one of the app's services (selectServicesByAppId).
//
// Unlike the service-scoped list, there is NO "Attach Disk" entry point here:
// the existing AttachDiskToServiceModal requires a single target `service`,
// and at the app level there are multiple services, so a clean app-level
// attach flow isn't available. Management from this surface = detach; attach
// happens at the service (or env) level.
import {
  estimateMonthlyCost,
  fetchDiskAttachments,
  fetchPersistentDisks,
  fetchServicesByAppId,
  selectDiskAttachmentsAsList,
  selectPersistentDisksAsList,
  selectServicesByAppId,
} from "@app/deploy";
import { useQuery, useSelector } from "@app/react";
import { schema } from "@app/schema";
import { capitalize } from "@app/string-utils";
import type { DeployDiskAttachment, DeployPersistentDisk } from "@app/types";
import { costColDef } from "@app/ui/shared/cost-estimate-tooltip";
import {
  CurrencyCell,
  DataTable,
  DataTableCell,
  type DataTableCellRendererProps,
  type DataTableColDef,
  DateTimeCell,
} from "@app/ui/shared/datatable";
import { DropdownMenu } from "@app/ui/shared/dropdown-menu";
import { Group } from "@app/ui/shared/group";
import { IconEllipsisHorizontal } from "@app/ui/shared/icons";
import { useMemo, useState } from "react";
import { DetachPersistentDiskModal } from "./detach-persistent-disk-modal";

interface AttachmentRow {
  attachment: DeployDiskAttachment;
  disk: DeployPersistentDisk | undefined;
  serviceHandle: string;
}

// Detach target for an app-scoped attachment row.
interface DetachTarget {
  disk: DeployPersistentDisk;
  attachment: DeployDiskAttachment;
  serviceHandle: string;
}

// Renders the cost for a single attached disk. "—" when the disk record is
// missing (e.g., not yet hydrated). Pulled out so the cost-rates selector can
// be read via a hook from inside the cell renderer.
const AttachmentCostCell = ({
  disk,
}: {
  disk: DeployPersistentDisk | undefined;
}) => {
  const rates = useSelector(schema.costRates.select);
  if (!disk) return <span className="text-gray-400">—</span>;
  const cost = estimateMonthlyCost({
    rates,
    persistentDisks: [{ size: disk.size, provisionedIops: 0 }],
  });
  return <CurrencyCell value={cost} />;
};

export const AppDiskAttachmentsList = ({
  appId,
}: {
  appId: string;
}) => {
  useQuery(fetchPersistentDisks());
  useQuery(fetchDiskAttachments());
  useQuery(fetchServicesByAppId({ id: appId }));

  const [detachTarget, setDetachTarget] = useState<DetachTarget | null>(null);

  const services = useSelector((s) => selectServicesByAppId(s, { appId }));
  const attachments = useSelector(selectDiskAttachmentsAsList);
  const disks = useSelector(selectPersistentDisksAsList);

  const rows = useMemo<AttachmentRow[]>(() => {
    const diskById = new Map(disks.map((d) => [d.id, d]));
    // Map each of the app's serviceIds to its handle so attachments can be
    // both filtered to this app and labeled with their owning service.
    const serviceHandleById = new Map(
      services.map((svc) => [svc.id, svc.handle]),
    );
    return attachments
      .filter((a) => a.status !== "deprovisioned")
      .filter((a) => serviceHandleById.has(a.serviceId))
      .map((a) => ({
        attachment: a,
        disk: diskById.get(a.persistentDiskId),
        serviceHandle: serviceHandleById.get(a.serviceId) ?? a.serviceId,
      }));
  }, [attachments, disks, services]);

  // Column order (cost is LAST before actions): Disk, Service, Mount Path,
  // Size, Status, Created, Cost/mo, Actions.
  const colDefs = useMemo<DataTableColDef<AttachmentRow>[]>(
    () => [
      {
        // Renderer-only columns reference nested data (attachment/disk), so
        // there's no flat row key to bind via `field` — use `colId` instead.
        colId: "disk",
        headerName: "Disk",
        cellRenderer: ({ data }: DataTableCellRendererProps<AttachmentRow>) => (
          <DataTableCell
            label={data.disk?.handle ?? data.attachment.persistentDiskId}
          />
        ),
      },
      {
        colId: "service",
        headerName: "Service",
        cellRenderer: ({ data }: DataTableCellRendererProps<AttachmentRow>) => (
          <DataTableCell label={data.serviceHandle} />
        ),
      },
      {
        colId: "mountPath",
        headerName: "Mount Path",
        cellRenderer: ({ data }: DataTableCellRendererProps<AttachmentRow>) => (
          <DataTableCell label={data.attachment.mountPoint} />
        ),
      },
      {
        colId: "size",
        headerName: "Size",
        cellRenderer: ({ data }: DataTableCellRendererProps<AttachmentRow>) => (
          <DataTableCell label={data.disk ? `${data.disk.size} GB` : "—"} />
        ),
      },
      {
        colId: "status",
        headerName: "Status",
        cellRenderer: ({ data }: DataTableCellRendererProps<AttachmentRow>) => (
          <DataTableCell label={capitalize(data.attachment.status)} />
        ),
      },
      {
        colId: "createdAt",
        headerName: "Created",
        cellRenderer: ({ data }: DataTableCellRendererProps<AttachmentRow>) => (
          <DateTimeCell date={data.attachment.createdAt} />
        ),
      },
      {
        ...costColDef,
        colId: "cost",
        cellRenderer: ({ data }: DataTableCellRendererProps<AttachmentRow>) => (
          <AttachmentCostCell disk={data.disk} />
        ),
      },
      {
        colId: "actions",
        headerName: "Actions",
        sortable: false,
        cellRenderer: ({ data }: DataTableCellRendererProps<AttachmentRow>) => (
          <DropdownMenu
            label={<IconEllipsisHorizontal variant="sm" />}
            items={[
              {
                id: "detach",
                label: "Detach",
                // Detach acts on this attachment. Disabled when the underlying
                // disk record isn't available (the detach modal needs it).
                disabled: !data.disk,
                onClick: (close) => {
                  if (data.disk) {
                    setDetachTarget({
                      disk: data.disk,
                      attachment: data.attachment,
                      serviceHandle: data.serviceHandle,
                    });
                  }
                  close();
                },
              },
            ]}
          />
        ),
      },
    ],
    [],
  );

  return (
    <Group>
      <DataTable<AttachmentRow> rowData={rows} colDefs={colDefs} />

      {detachTarget ? (
        <DetachPersistentDiskModal
          isOpen={detachTarget !== null}
          disk={detachTarget.disk}
          attachment={detachTarget.attachment}
          serviceHandle={detachTarget.serviceHandle}
          onClose={() => setDetachTarget(null)}
        />
      ) : null}
    </Group>
  );
};
